'use client';
import { useAnalyticsPageView } from '../components/PageView/Context';
import { getCleanLocationHref } from '../lib/urlUtils';
import { useAnalytics } from './useAnalytics';

export type PageActionProps = {
  actionInput?: string | null;
  actionName?: string | null;
  actionSection?: string;
  actionType?: string;
  activityId?: string;
  destinationURL?: string;
  impressionSection?: string;
  impressionPosition?: number;
  parentActivityId?: string;
  targetPageURL?: string;
  additionalAttributes?: object;
  pageBranchName?: string | null;
};

export type PageActionPayload = {
  action_input: string | null | undefined;
  action_name: string | null | undefined;
  action_section: string | undefined;
  action_type: string | undefined;
  activity_id: string | undefined;
  destination_url: string | undefined;
  parent_activity_id: string | undefined;
  action_location_url: string | undefined;
  target_page_url: string | undefined;
  deployable_name: string | undefined;
  deployable_version: string | undefined;
  impression_page_name: string | undefined;
  impression_page_url: string | undefined;
  impression_pageview_id: string | undefined;
  impression_section: string | undefined;
  impression_position: number | undefined;
  traditional_device_id: string | undefined;
  additional_attributes: object | undefined;
  page_branch_name: string | null | undefined;
};

export const usePageAction = ({
  actionInput,
  actionName,
  actionSection,
  actionType,
  activityId,
  destinationURL,
  impressionSection,
  impressionPosition,
  parentActivityId,
  targetPageURL,
  pageBranchName,
  additionalAttributes,
}: PageActionProps) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const { pageViewId, pageName } = useAnalyticsPageView();

  const isClient = typeof window !== 'undefined';

  const segmentProps: PageActionPayload = {
    action_input: actionInput,
    action_name: actionName,
    action_section: actionSection,
    action_type: actionType,
    activity_id: activityId,
    destination_url: destinationURL,
    parent_activity_id: parentActivityId,
    action_location_url: isClient ? window.location.href : undefined,
    target_page_url: targetPageURL,
    deployable_name: deployableName,
    deployable_version: deployableVersion,
    impression_page_name: pageName || (isClient ? window.location.pathname : undefined),
    impression_page_url: isClient ? getCleanLocationHref() : undefined,
    impression_pageview_id: pageViewId,
    impression_section: impressionSection,
    impression_position: impressionPosition,
    traditional_device_id: deviceId,
    page_branch_name: pageBranchName,
    additional_attributes: additionalAttributes,
  };

  return (overrideProps?: Partial<PageActionPayload> | null, callback?: () => void) =>
    analytics &&
    void analytics
      .track(
        'Page Action',
        {
          ...segmentProps,
          ...overrideProps,
        },
        { context: { app: { name: deployableName, version: gitSha } } },
      )
      ?.finally(() => {
        callback?.();
      });
};

export type PageActionFireFn = ReturnType<typeof usePageAction>;
